import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import { getPointOfSales } from '../../../services/pointOfSalesService';
import { salesCashLiquidationService } from '../../../services/salesCashLiquidationService';
import { downloadCashLiquidationPdf } from '../../../utils/generateCashLiquidationPdf';

const STORAGE_KEY = 'ventas_reports_cash_liquidations_filters';

function defaultDates() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultDates(), pointOfSaleId: '' };
    const parsed = JSON.parse(raw);
    return {
      dateFrom: parsed.dateFrom || defaultDates().dateFrom,
      dateTo: parsed.dateTo || defaultDates().dateTo,
      pointOfSaleId: parsed.pointOfSaleId || '',
    };
  } catch {
    return { ...defaultDates(), pointOfSaleId: '' };
  }
}

function saveStoredFilters(filters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

function formatMoney(amount) {
  return `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return '—';
  const [y, m, d] = String(value).split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}/${y}`;
}

export default function VentasCashLiquidationsReport() {
  const { loaded, ventasCashLiquidationEnabled } = useModuleSettings();
  const [filters, setFilters] = useState(loadStoredFilters);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pdfId, setPdfId] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getPointOfSales()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setPointsOfSale(res.data.filter((p) => p.is_active !== false));
        }
      })
      .catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
    setPage(1);
  }, []);

  const loadData = useCallback(async () => {
    if (!ventasCashLiquidationEnabled) {
      setRows([]);
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const params = {
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        per_page: 40,
        page,
      };
      if (filters.pointOfSaleId) {
        params.point_of_sale_id = filters.pointOfSaleId;
      }
      const res = await salesCashLiquidationService.list(params);
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setSummary(res.summary || null);
        setMeta(res.meta || null);
      } else {
        setRows([]);
        setSummary(null);
        setToast({ isOpen: true, message: res.message || 'Error al cargar el reporte', type: 'error' });
      }
    } catch (error) {
      setRows([]);
      setSummary(null);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar liquidaciones',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, page, ventasCashLiquidationEnabled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const downloadPdf = async (row) => {
    if (!row?.shift_date || !row?.point_of_sale_id) return;
    setPdfId(row.id);
    try {
      const res = await salesCashLiquidationService.get({
        point_of_sale_id: row.point_of_sale_id,
        shift_date: row.shift_date,
      });
      if (!res.success || !res.data) {
        throw new Error(res.message || 'No se pudo cargar la liquidación');
      }
      await downloadCashLiquidationPdf(res.data);
      setToast({ isOpen: true, message: 'Reporte de cierre descargado', type: 'success' });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || error.message || 'No se pudo generar el PDF',
        type: 'error',
      });
    } finally {
      setPdfId(null);
    }
  };

  if (loaded && !ventasCashLiquidationEnabled) {
    return <Navigate to="/ventas/reports" replace />;
  }

  const lastPage = meta?.last_page || 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1200px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">
              Dashboard
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/orders" className="hover:text-teal-600">
              Ventas
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/reports" className="hover:text-teal-600">
              Reportes
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Liquidaciones de caja</span>
          </nav>

          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Liquidaciones de caja cerradas</h1>
              <p className="text-gray-600 mt-1 max-w-3xl">
                Consulte los cierres guardados por rango de fechas y punto de venta. Puede abrir el
                detalle o descargar el PDF oficial de cada liquidación.
              </p>
            </div>
            <Link
              to="/ventas/caja"
              className="px-4 py-2 text-sm font-medium border border-teal-300 text-teal-800 bg-teal-50 rounded-lg hover:bg-teal-100"
            >
              Ir a liquidación del día
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Punto de venta</label>
                <select
                  value={filters.pointOfSaleId}
                  onChange={(e) => updateFilter('pointOfSaleId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos los POS</option>
                  {pointsOfSale.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.code} — {pos.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={loadData}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
                >
                  {loading ? 'Cargando…' : 'Consultar'}
                </button>
              </div>
            </div>
          </div>

          {summary ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-6">
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <p className="text-xs font-medium text-gray-500 uppercase">Cierres</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.liquidations_count || 0}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <p className="text-xs font-medium text-gray-500 uppercase">Saldos iniciales</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatMoney(summary.opening_total)}</p>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                <p className="text-xs font-medium text-emerald-700 uppercase">Efectivo cobrado</p>
                <p className="text-xl font-bold text-emerald-900 mt-1">
                  {formatMoney(summary.cash_sales_total)}
                </p>
              </div>
              <div className="rounded-xl border border-sky-100 bg-sky-50 px-4 py-3">
                <p className="text-xs font-medium text-sky-700 uppercase">Ajustes</p>
                <p className="text-xl font-bold text-sky-900 mt-1">
                  {formatMoney(summary.adjustment_total)}
                </p>
              </div>
              <div className="rounded-xl border border-teal-100 bg-teal-50 px-4 py-3">
                <p className="text-xs font-medium text-teal-700 uppercase">Saldos finales</p>
                <p className="text-xl font-bold text-teal-900 mt-1">
                  {formatMoney(summary.closing_total)}
                </p>
              </div>
            </div>
          ) : null}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            {loading && !rows.length ? (
              <p className="text-sm text-gray-500 py-10 text-center">Cargando liquidaciones…</p>
            ) : !rows.length ? (
              <p className="text-sm text-gray-500 py-10 text-center">
                No hay liquidaciones cerradas en el rango seleccionado.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-500 border-b">
                        <th className="py-2 pr-3">Fecha</th>
                        <th className="py-2 pr-3">POS</th>
                        <th className="py-2 pr-3 text-right">Saldo inicial</th>
                        <th className="py-2 pr-3 text-right">Efectivo</th>
                        <th className="py-2 pr-3 text-right">Ajuste</th>
                        <th className="py-2 pr-3 text-right">Saldo final</th>
                        <th className="py-2 pr-3">Usuario</th>
                        <th className="py-2 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const adj = Number(row.cash_adjustment || 0);
                        return (
                          <tr key={row.id} className="border-b border-gray-100">
                            <td className="py-2.5 pr-3 font-medium text-gray-900">
                              {formatDate(row.shift_date)}
                            </td>
                            <td className="py-2.5 pr-3 text-gray-700">
                              {row.point_of_sale
                                ? `${row.point_of_sale.code} — ${row.point_of_sale.name}`
                                : '—'}
                            </td>
                            <td className="py-2.5 pr-3 text-right">{formatMoney(row.opening_balance)}</td>
                            <td className="py-2.5 pr-3 text-right text-emerald-800 font-medium">
                              {formatMoney(row.cash_sales)}
                            </td>
                            <td className="py-2.5 pr-3 text-right">
                              {Math.abs(adj) >= 0.01 ? formatMoney(adj) : '—'}
                            </td>
                            <td className="py-2.5 pr-3 text-right font-semibold text-teal-900">
                              {formatMoney(row.closing_balance)}
                            </td>
                            <td className="py-2.5 pr-3 text-gray-600">{row.saved_by_name || '—'}</td>
                            <td className="py-2.5 text-right whitespace-nowrap">
                              <Link
                                to={`/ventas/caja?pos=${row.point_of_sale_id}&date=${row.shift_date}`}
                                className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 font-medium mr-3"
                                title="Ver detalle de la liquidación"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                Ver
                              </Link>
                              <button
                                type="button"
                                onClick={() => downloadPdf(row)}
                                disabled={pdfId === row.id}
                                className="text-red-600 hover:text-red-800 font-medium disabled:opacity-60"
                                title="Descargar PDF de cierre"
                              >
                                {pdfId === row.id ? 'PDF…' : 'PDF'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 ? (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Página {meta?.current_page || page} de {lastPage} · {meta?.total || rows.length}{' '}
                      registro(s)
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        type="button"
                        disabled={page >= lastPage || loading}
                        onClick={() => setPage((p) => p + 1)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
}
